import React, { useState, useCallback, useEffect } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Modal } from 'react-bootstrap';
import axios from 'axios';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';

export const route = "/mimo/daily-sales";
export const role = "MIMO";

function DailySales() {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [transactionDate, setTransactionDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [sortField, setSortField] = useState('DISPLAY_DATE');
    const [sortOrder, setSortOrder] = useState('ASC');
    const [showModal, setShowModal] = useState(false);
    const [entries, setEntries] = useState('all');

    const handleExportClick = () => {
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleExport = async () => {
        try {
            const url = `${process.env.REACT_APP_SNOWFLAKE_URL}/mimo/daily-sales/${transactionDate}?sortField=${sortField}&sortOrder=${sortOrder}`;

            const response = await axios.get(url);
            let data = response.data;

            if (!data || data.length === 0) {
                alert('No data found for the selected date.');
                handleModalClose();
                return;
            }

            // Define headers for the Excel export
            const headers = [
                'Date', 'Location', 'Item Description', 'Category', 'Quantity', 
                'Net Sales', 'FB Credit', 'Tax', 'Additional Fees', 'Gross Sales', 
                'Commission', 'Gross Net Sales', 'Ticket Count', 'Rider Count'
            ];

            // Format data for Excel
            const orderedData = data.map(sale => [
                sale.DISPLAY_DATE ? `${sale.DISPLAY_DATE.substring(5, 7)}/${sale.DISPLAY_DATE.substring(8, 10)}/${sale.DISPLAY_DATE.substring(0, 4)}` : '',
                sale.LOCATION,
                sale.ITEM_DESCRIPTION,
                sale.CATEGORY,
                sale.QUANTITY,
                sale.NET_SALES,
                sale.FB_CREDIT,
                sale.TAX,
                sale.ADDITIONAL_FEES,
                sale.GROSS_SALES,
                sale.COMMISSION,
                sale.GROSS_NET_SALES,
                sale.TICKET_COUNT,
                sale.RIDER_COUNT
            ]);

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([]);

            // Add metadata to the worksheet
            XLSX.utils.sheet_add_aoa(worksheet, [
                [`Daily Sales Report`],
                [`Date Created: ${format(new Date(), 'MM/dd/yyyy')}`],
                [`Sales Date: ${transactionDate.substring(5, 7)}/${transactionDate.substring(8, 10)}/${transactionDate.substring(0, 4)}`],
                []
            ], { origin: 'A1' });

            // Set headers on line 5
            XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A5' });

            // Add the data starting on line 6
            XLSX.utils.sheet_add_aoa(worksheet, orderedData, { origin: 'A6' });

            // Set column widths
            worksheet['!cols'] = Array(headers.length).fill({ width: 15 });

            XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Sales');
            XLSX.writeFile(workbook, `MIMO_Daily_Sales_${transactionDate}.xlsx`);

        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        }

        handleModalClose();
    };

    const fetchSales = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = `${process.env.REACT_APP_SNOWFLAKE_URL}/mimo/daily-sales/${transactionDate}?sortField=${sortField}&sortOrder=${sortOrder}`;

            const response = await axios.get(url);
            setSales(response.data);
        } catch (error) {
            console.error('Error fetching sales data:', error);
            setError('Failed to fetch sales data');
        } finally {
            setLoading(false);
        }
    }, [transactionDate, sortField, sortOrder]);

    useEffect(() => {
        fetchSales();
    }, [transactionDate, fetchSales]);

    const handleSort = (field) => {
        setSortField(field);
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '$0.00';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return parseInt(num).toLocaleString();
    };

    return (
        <div className="container mt-5">
            <h1>Daily Sales Report</h1>

            <Form>
                <Row className="mb-3 align-items-end">
                    <Col md={4}>
                        <Form.Group controlId="transactionDate">
                            <Form.Label>Transaction Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={transactionDate}
                                onChange={(e) => setTransactionDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Button variant="success" onClick={handleExportClick} className="w-100">
                            Export to Excel
                        </Button>
                    </Col>
                </Row>
            </Form>

            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Export Daily Sales Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="formEntries">
                        <Form.Label>Number of Entries</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter number of entries or 'all'"
                            value={entries}
                            onChange={(e) => setEntries(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleExport}>
                        Export
                    </Button>
                </Modal.Footer>
            </Modal>

            {loading ? (
                <Spinner animation="border" variant="primary" />
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : sales.length === 0 ? (
                <Alert variant="info">No sales data found for the selected date</Alert>
            ) : (
                <>
                    <div className="mb-3">
                        <strong>Total Records: {sales.length}</strong>
                    </div>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('DISPLAY_DATE')} style={{cursor: 'pointer'}}>
                                    Date {sortField === 'DISPLAY_DATE' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('LOCATION')} style={{cursor: 'pointer'}}>
                                    Location {sortField === 'LOCATION' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('ITEM_DESCRIPTION')} style={{cursor: 'pointer'}}>
                                    Item Description {sortField === 'ITEM_DESCRIPTION' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('CATEGORY')} style={{cursor: 'pointer'}}>
                                    Category {sortField === 'CATEGORY' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('QUANTITY')} style={{cursor: 'pointer'}}>
                                    Quantity {sortField === 'QUANTITY' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('NET_SALES')} style={{cursor: 'pointer'}}>
                                    Net Sales {sortField === 'NET_SALES' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('FB_CREDIT')} style={{cursor: 'pointer'}}>
                                    FB Credit {sortField === 'FB_CREDIT' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('TAX')} style={{cursor: 'pointer'}}>
                                    Tax {sortField === 'TAX' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('ADDITIONAL_FEES')} style={{cursor: 'pointer'}}>
                                    Additional Fees {sortField === 'ADDITIONAL_FEES' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('GROSS_SALES')} style={{cursor: 'pointer'}}>
                                    Gross Sales {sortField === 'GROSS_SALES' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('COMMISSION')} style={{cursor: 'pointer'}}>
                                    Commission {sortField === 'COMMISSION' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('GROSS_NET_SALES')} style={{cursor: 'pointer'}}>
                                    Gross Net Sales {sortField === 'GROSS_NET_SALES' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('TICKET_COUNT')} style={{cursor: 'pointer'}}>
                                    Ticket Count {sortField === 'TICKET_COUNT' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('RIDER_COUNT')} style={{cursor: 'pointer'}}>
                                    Rider Count {sortField === 'RIDER_COUNT' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map((sale, index) => (
                                <tr key={`${sale.DISPLAY_DATE}-${sale.LOCATION}-${sale.ITEM_DESCRIPTION}-${index}`}>
                                    <td>{sale.DISPLAY_DATE ? `${sale.DISPLAY_DATE.substring(5, 7)}/${sale.DISPLAY_DATE.substring(8, 10)}/${sale.DISPLAY_DATE.substring(0, 4)}` : ''}</td>
                                    <td>{sale.LOCATION}</td>
                                    <td>{sale.ITEM_DESCRIPTION}</td>
                                    <td>{sale.CATEGORY}</td>
                                    <td className="text-end">{formatNumber(sale.QUANTITY)}</td>
                                    <td className="text-end">{formatCurrency(sale.NET_SALES)}</td>
                                    <td className="text-end">{formatCurrency(sale.FB_CREDIT)}</td>
                                    <td className="text-end">{formatCurrency(sale.TAX)}</td>
                                    <td className="text-end">{formatCurrency(sale.ADDITIONAL_FEES)}</td>
                                    <td className="text-end">{formatCurrency(sale.GROSS_SALES)}</td>
                                    <td className="text-end">{formatCurrency(sale.COMMISSION)}</td>
                                    <td className="text-end">{formatCurrency(sale.GROSS_NET_SALES)}</td>
                                    <td className="text-end">{formatNumber(sale.TICKET_COUNT)}</td>
                                    <td className="text-end">{formatNumber(sale.RIDER_COUNT)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    );
}

export default DailySales;